import React, { useState, useEffect } from 'react';
import { Box } from '@mui/joy';
import Header from './Header';
import Footer from './Footer';
import SessionExpiredModal from './SessionExpiredModal';
import InsufficientPermissionsModal from './InsufficientPermissionsModal';
import { SessionProvider } from '../../../context/SessionContext';
import { EmployerProvider, useEmployer } from '../../../context/EmployerContext';
import { MeetingProvider } from '../../../context/MeetingContext';

interface LayoutProps {
  children: React.ReactNode;
  showHeader?: boolean;
  showFooter?: boolean;
}

export const HEADER_HEIGHT = '72px';

// Wrapper component that provides MeetingProvider with employer's address
const MeetingProviderWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { employer } = useEmployer();
  
  return (
    <MeetingProvider initialAddress={employer?.companyAddress || ''}>
      {children}
    </MeetingProvider>
  );
};

const Layout: React.FC<LayoutProps> = ({ children, showHeader = true, showFooter = true }) => {
  const [showSessionExpiredModal, setShowSessionExpiredModal] = useState(false);
  const [showInsufficientPermissionsModal, setShowInsufficientPermissionsModal] = useState(false);

  useEffect(() => {
    const handleSessionExpired = () => {
      setShowSessionExpiredModal(true);
    };
    const handleInsufficientPermissions = () => {
      setShowInsufficientPermissionsModal(true);
    };

    window.addEventListener('sessionExpired', handleSessionExpired);
    window.addEventListener('insufficientPermissions', handleInsufficientPermissions);

    return () => {
      window.removeEventListener('sessionExpired', handleSessionExpired);
      window.removeEventListener('insufficientPermissions', handleInsufficientPermissions);
    };
  }, []);

  const handleCloseSessionModal = () => {
    setShowSessionExpiredModal(false);
  };
  const handleCloseInsufficientPermissionsModal = () => {
    setShowInsufficientPermissionsModal(false);
  };

  return (
    <SessionProvider>
      <EmployerProvider>
        <MeetingProviderWrapper>
          <Box sx={{ minHeight: '100vh' }}>
            {showHeader && <Header />}
            <Box sx={{ pt: showHeader ? HEADER_HEIGHT : 0 }}>
              {children}
            </Box>
            {showFooter && <Footer />}
            <SessionExpiredModal 
              open={showSessionExpiredModal} 
              onClose={handleCloseSessionModal} 
            />
            <InsufficientPermissionsModal
              open={showInsufficientPermissionsModal}
              onClose={handleCloseInsufficientPermissionsModal}
            />
          </Box>
        </MeetingProviderWrapper>
      </EmployerProvider>
    </SessionProvider>
  );
};

export default Layout;